import {api} from "@/shared/api";

import {BasicDataTable, BasicDataTableConfig, TypedColumnSettings} from "@/shared/ui/basicDataTable"
import {WorkingTimeRequestStepsConfigurationComponent} from "@/features/requests/workingTimeRequestStepsConfigurationComponent";
import {ButtonRadioGroup} from "@/shared/ui/buttonRadioGroup";
import {FieldObserver} from "@/shared/ui/fieldObserver";
import {FormUtil} from "@/shared/forms/formUtil";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {copyOfObject, serializeObjectToArray} from "@/shared/utils/misc";
import {SecurityStatus} from "@/shared/securityStatus";
import {
	AutomaticBreakDeductionPolicy,
	CompensationType,
	DurationUnit,
	NormalWorkingTimeEvaluationMode,
	ProofRequirement,
	TravellingTimeTargetAccount,
	WorkingTimeNoticeMode,
	WorkingTimeTypeCategory,
	WorkingTimeTypeRecordingMode,
	WorkingTimeTypeSubCategory
} from "@/shared/timrTypes";
import {ErrorResponse, WorkingTimeRequestStepConfiguration, WorkingTimeType} from "@/shared/timr";
import {LocationPanel} from "@/shared/locationPanel";
import {
	CompensationCreditabilityMode,
	CreditabilityCompensationComponent
} from "./workingTimeType/creditabilityCompensationComponent";
import * as Constants from "@/shared/constants";
import {getProofRequirementOptions} from "./workingTimeType/proofRequirementOptions";

declare const CATEGORY_UNITS: Record<WorkingTimeTypeCategory, DurationUnit[]>;

interface WorkingTimeTypesConfig {
	defaultWorkingTimeRequestStepsConfiguration: WorkingTimeRequestStepConfiguration[];
	defaultWorkingTimeRequestStepsEmailNotificationEnabled: boolean;
	billingAddressCountryId?: number;
}

export class WorkingTimeTypesAdministrationPage {
	private table: WorkingTimeTypesTable;
	private dialogHandler: WorkingTimeTypesDialogHandler;

	constructor(config: WorkingTimeTypesConfig) {
		this.table = new WorkingTimeTypesTable({
			element: $("#workingTimeTypesTable"),
			orderable: false,
			allowExpand: true,
			clampNote: true,
			initialFilter: this.buildTableFilterData()
		});
		$(this.table)
			.on(BasicDataTable.EVENT_DBLCLICK + " " + WorkingTimeTypesTable.EVENT_WORKING_TIME_TYPE_EDIT, (_e, workingTimeType: WorkingTimeType) => {
				this.dialogHandler.setWorkingTimeType(workingTimeType);
				this.dialogHandler.openDialog();
			})
			.on(WorkingTimeTypesTable.EVENT_WORKING_TIME_TYPE_DELETE, (_e, workingTimeType: WorkingTimeType) => {
				this.deleteWorkingTimeType(workingTimeType);
			});

		this.dialogHandler = new WorkingTimeTypesDialogHandler(config);
		$(this.dialogHandler)
			.on(WorkingTimeTypesDialogHandler.EVENT_WORKING_TIME_TYPE_UPDATED, () => {
				this.table.filter(this.buildTableFilterData());
			})
			.on(WorkingTimeTypesDialogHandler.EVENT_WORKING_TIME_TYPE_DELETE, (_e, workingTimeType: WorkingTimeType) => {
				this.deleteWorkingTimeType(workingTimeType);
			});


		$("#workingTimeTypesFilterArchived").change(() => {
			this.table.filter(this.buildTableFilterData());
		});

		// listeners for compensation type radio selection
		$("input[type=radio][name^=compensationType]").change(() => {
			this.dialogHandler.creditabilityConfigChanged();
		});

		$("#workingTimeTypeAddButton").click(() => {
			if (!SecurityStatus.isAddWorkingTimeTypeAllowed()) {
				BootstrapModal.warn({
					title: "limit.workingTimeType.title",
					message: "limit.workingTimeType"
				});
				return;
			}

			this.dialogHandler.resetWorkingTimeType();
			this.dialogHandler.openDialog();
		});
	}

	private deleteWorkingTimeType(workingTimeType: WorkingTimeType) {
		BootstrapModal.confirm({
			title: "dialog.warning.title",
			message: messages.withArguments("dialog.delete.workingTimeType.message", workingTimeType.name),
			yesLabel: "confirmation.delete",
			yesAction: (modal) => {
				api("admin/deleteWorkingTimeType.json")
					.post({
						workingTimeTypeId: workingTimeType.id
					})
					.then(data => {
						SecurityStatus.update(data.securityStatus);
						this.table.reload();
						this.dialogHandler.close();
						modal.close();
					});
			}
		});
	}

	private buildTableFilterData() {
		return [
			{
				name: "archived",
				value: $("#workingTimeTypesFilterArchived").val() as string
			}
		];
	}
}

class WorkingTimeTypesTable extends BasicDataTable<WorkingTimeType> {
	public static EVENT_WORKING_TIME_TYPE_EDIT = "eventTypeEdit";
	public static EVENT_WORKING_TIME_TYPE_DELETE = "eventTypeDelete";

	constructor(config: BasicDataTableConfig) {
		super("/timr/admin/workingTimeTypes.json", config);
	}

	protected createContextMenu() {
		return super.createContextMenu()
			.addMenuEntry({
				label: "workingTimeTypes.context.edit",
				action: type => { $(this).triggerHandler(WorkingTimeTypesTable.EVENT_WORKING_TIME_TYPE_EDIT, type); }
			})
			.addMenuEntry({
				label: "workingTimeTypes.context.delete",
				action: type => { $(this).triggerHandler(WorkingTimeTypesTable.EVENT_WORKING_TIME_TYPE_DELETE, type); }
			})
			.addMenuEntry({
				label: "workingTimeTypes.context.archive",
				action: type => this.archiveWorkingTimeType(type),
				show: type => !type.archived
			})
			.addMenuEntry({
				label: "workingTimeTypes.context.unarchive",
				action: type => this.unarchiveWorkingTimeType(type),
				show: type => type.archived
			});
	}

	private archiveWorkingTimeType(workingTimeType: WorkingTimeType) {
		api("admin/archiveWorkingTimeType.json")
			.post({
				id: workingTimeType.id
			})
			.then((data) => {
				SecurityStatus.update(data.securityStatus);
				this.reload();
			});
	}

	private unarchiveWorkingTimeType(workingTimeType: WorkingTimeType) {
		if (!SecurityStatus.isAddWorkingTimeTypeAllowed()) {
			BootstrapModal.warn({
				title: "limit.workingTimeType.title",
				message: "limit.workingTimeType"
			});
			return;
		}

		api("admin/unarchiveWorkingTimeType.json")
			.post({
				id: workingTimeType.id
			})
			.then((data) => {
				SecurityStatus.update(data.securityStatus);
				this.reload();
			});
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			dom: `rt<"datatable-footer"ip>`,
			paging: true,
			serverSide: true,
			rowCallback: (row: Node, data) => {
				const workingTimeType = data as WorkingTimeType;
				if (workingTimeType.archived) {
					$(row).addClass("archived");
				}
			},
			pageLength: Constants.ADMINISTRATION_MAIN_TABLES_PAGE_LENGTH
		};
	}

	protected getColumnSettings(): TypedColumnSettings<WorkingTimeType>[] {
		return [
			{data: "id", width: "44px", orderable: false, className: "context-menu", render: this.contextMenu.render},
			{data: "name", orderable: true, render: BasicDataTable.renderText},
			{data: "shortName", orderable: true, render: BasicDataTable.renderText},
			{data: "description", orderable: false, render: BasicDataTable.renderText},
			{data: "category", orderable: false, render: this.renderWorkingTimeTypeCategory},
			{data: "editUnit", orderable: false, render: this.renderEditUnit},
			{data: "recordingModeUser", orderable: false, render: this.renderRecordingModeUser},
			{
				data: "archived",
				orderable: false,
				render: this.renderArchived,
				className: "slim-icon-column",
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			}
		];
	}

	private renderArchived(this: void, data: boolean, _type: string, _recording: WorkingTimeType, _meta: DataTables.CellMetaSettings): string {
		return data ? `<i class="fal fa-archive" title="${messages["workingTimeType.archived"]}"></i>` : "";
	}

	private renderWorkingTimeTypeCategory(this: void, data: WorkingTimeTypeCategory, _type: string, _recording: WorkingTimeType, _meta: DataTables.CellMetaSettings): string {
		return messages["workingTimeTypeCategory." + data];
	}

	private renderEditUnit(this: void, data: DurationUnit, _type: string, _recording: WorkingTimeType, _meta: DataTables.CellMetaSettings): string {
		return messages["durationUnit." + data];
	}

	private renderRecordingModeUser(this: void, data: WorkingTimeTypeRecordingMode, _type: string, _recording: WorkingTimeType, _meta: DataTables.CellMetaSettings): string {
		return data ? messages["workingTimeType.recordingModeUser." + data] : "";
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[1, "asc"]];
	}
}

class WorkingTimeTypesDialogHandler {
	public static EVENT_WORKING_TIME_TYPE_UPDATED = "eventWorkingTimeTypeUpdated";
	public static EVENT_WORKING_TIME_TYPE_DELETE = "eventWorkingTimeTypeDelete";

	private readonly modal: BootstrapModal.Modal;
	private workingTimeType: WorkingTimeType;
	private compensationCreditabilityChanged: boolean = false;
	private automaticBreakDeductionPolicyChanged: boolean = false;
	private locationPanel: LocationPanel | null = null;
	private workingTimeRequestStepsConfigurationComponent: WorkingTimeRequestStepsConfigurationComponent | null;
	private normalWorkingTimeEvaluationModeButtonGroup: ButtonRadioGroup<NormalWorkingTimeEvaluationMode>;
	private creditabilityCompensationComponent: CreditabilityCompensationComponent | null;
	private travellingTimeNWTIndependentCreditabilityCompensationComponent: CreditabilityCompensationComponent | null;
	private travellingTimeOutsideNWTCreditabilityCompensationComponent: CreditabilityCompensationComponent | null;

	private observer: FieldObserver<WorkingTimeType>;

	private config: WorkingTimeTypesConfig;

	constructor(config: WorkingTimeTypesConfig) {
		this.config = config;
		this.modal = new BootstrapModal.Modal({
			title: "workingTimeType.header.create",
			message: $("#workingTimeTypeDialog"),
			destroyOnClose: false,
			size: "modal-xl",
			buttons: [{
				text: "dialog.save",
				css: "btn-primary",
				action: this.checkAndSaveWorkingTimeType
			}, {
				text: "dialog.cancel"
			}, {
				text: "dialog.delete",
				css: "btn-outline-danger",
				left: true,
				action: () => { $(this).triggerHandler(WorkingTimeTypesDialogHandler.EVENT_WORKING_TIME_TYPE_DELETE, this.workingTimeType); }
			}],
			onOpen: () => {
				$("#workingTimeTypeName").focus();
			},
			onClose: () => {
				FormUtil.switchTabNavigationToFirst($("#wttTabNavigation"));
			}
		});

		this.observer = new FieldObserver<WorkingTimeType>();

		this.initCreditabilityCompensationComponent();
		this.initTravellingTimeNormalWorkingTimeSelection();
		this.initStepConfiguration();
		this.initLocationTabFields();
		this.resetWorkingTimeType();
		this.initListener();
		this.initPopups();
	}

	private initCreditabilityCompensationComponent() {
		this.creditabilityCompensationComponent = document.getElementById("creditabilityCompensationComponent") as CreditabilityCompensationComponent;
		this.addCreditabilityChangeListener(this.creditabilityCompensationComponent);
	}

	private initStepConfiguration() {
		this.workingTimeRequestStepsConfigurationComponent = document.getElementById("workingTimeRequestStepsConfigurationComponent") as WorkingTimeRequestStepsConfigurationComponent;
		if (this.workingTimeRequestStepsConfigurationComponent) {
			this.workingTimeRequestStepsConfigurationComponent.config = this.config.defaultWorkingTimeRequestStepsConfiguration;
		}

		const approvalStepsSelect: HTMLSelectElement | null = document.getElementById("selectIndividualApproval") as HTMLSelectElement;
		approvalStepsSelect?.addEventListener("change", () => {
			$("[data-visible-for-individual]").slideToggleBool(approvalStepsSelect.value === "INDIVIDUAL");
		});
	}

	private checkAndSaveWorkingTimeType = () => {
		const button = this.modal.getButtons("btn-primary");
		Button.setLoading(button);

		if (this.workingTimeType.id && (this.compensationCreditabilityChanged || this.automaticBreakDeductionPolicyChanged)) {
			api("admin/checkForWorkingTimeTypeChange.json")
				.get(this.workingTimeType)
				.then(data => {
					if (data.foundWorkingTimes > 0) {
						BootstrapModal.confirm({
							title: "dialog.update.workingTimeType.title",
							message: this.buildWorkingTimeTypeUpdateMessage(this.compensationCreditabilityChanged, this.automaticBreakDeductionPolicyChanged),
							escapeText: false,
							yesLabel: "dialog.update.workingTimeType.yes",
							yesAction: (modal) => {
								this.sendSaveWorkingTimeTypeRequest([modal, this.modal]);
							},
							noLabel: "dialog.update.workingTimeType.no",
							noAction: (modal) => {
								modal.close();
								Button.resetLoading(button);
							}
						});
					} else {
						this.sendSaveWorkingTimeTypeRequest([this.modal]);
					}
				})
				.fail((response: ErrorResponse) => {
					if (response.responseJSON?.fieldErrors) {
						FormUtil.markFieldErrors($("#workingTimeTypeDialog"), response.responseJSON.fieldErrors, true);
					}
					Button.resetLoading(button);
				});
		} else {
			this.sendSaveWorkingTimeTypeRequest([this.modal]);
		}
	}

	private buildWorkingTimeTypeUpdateMessage(compensationCreditabilityChanged: boolean, automaticBreakDeductionPolicyChanged: boolean): string {
		let message = "";
		if (compensationCreditabilityChanged) {
			message += messages["dialog.update.workingTimeType.message.creditability"];
		}
		if (automaticBreakDeductionPolicyChanged) {
			if (message) {
				message += "<br /><br />";
			}
			message += messages["dialog.update.workingTimeType.message.automaticBreakDeductionPolicy"];
		}
		message += "<br /><br />" + messages["dialog.update.workingTimeType.message.balanceInfo"]
		return message;
	}

	private sendSaveWorkingTimeTypeRequest(modals: BootstrapModal.Modal[]) {
		FormUtil.removeAllFeedback($("#workingTimeTypeDialog"), true);

		api("admin/saveWorkingTimeType.json")
			.post(serializeObjectToArray({...this.prepareWorkingTimeTypeForSave()})) // send the working time type with creditability and target travelling time account values
			.done((data) => {
				SecurityStatus.update(data.securityStatus);
				$(this).triggerHandler(WorkingTimeTypesDialogHandler.EVENT_WORKING_TIME_TYPE_UPDATED);
				modals.forEach(modal => modal.close());
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors($("#workingTimeTypeDialog"), response.responseJSON.fieldErrors, true);
				}
			})
			.always(() => {
				modals.forEach(modal => Button.resetLoading(modal.getButtons("btn-primary")));
			});
	}


	/**
	 * Returns `WorkingTimeType` object to be saved for request containing compensation and creditability values and also
	 * travelling time target accounts based on the current settings
	 *
	 * If enhanced working time types and travelling times are not allowed, the method returns the current `workingTimeType` without any changes.
	 *
	 * If the working time type category is `BreakTime`, all creditability and deductible
	 * values are set to 0.
	 */
	private prepareWorkingTimeTypeForSave(): WorkingTimeType {
		let workingTimeType: WorkingTimeType;

		if (SecurityStatus.isEnhancedWorkingTimeTypesAllowed()) {
			// If the working time type is a BreakTime, set all creditability and deductible values to 0
			if (this.workingTimeType.category === WorkingTimeTypeCategory.BreakTime) {
				workingTimeType = {
					...this.workingTimeType,
					creditability: 0,
					nonCreditableDeductible: 0,
					compensationCreditability: 0,
					compensationDeductible: 0
				};
			} else if (this.creditabilityCompensationComponent
				&& this.workingTimeType.category !== WorkingTimeTypeCategory.PaidTimeOff) {
				workingTimeType = this.setCreditabilityCompensationProperties(this.creditabilityCompensationComponent);
			} else {
				workingTimeType = this.workingTimeType;
			}
		} else {
			// don't change creditability / nonCreditabileDeductible if enhanced working time types are not allowed
			workingTimeType = this.workingTimeType;
		}

		if (this.isTravellingTime() && SecurityStatus.isTravellingTimeAllowed()) {
			const selectedTargetAccountWithin: TravellingTimeTargetAccount = $(`input[type="radio"][name="travellingTimeTargetAccountWithinNormalWorkingTime"]:checked`).val() as TravellingTimeTargetAccount;
			const selectedTargetAccountOutside: TravellingTimeTargetAccount = $(`input[type="radio"][name="travellingTimeTargetAccountOutsideNormalWorkingTime"]:checked`).val() as TravellingTimeTargetAccount;
			if (this.normalWorkingTimeEvaluationModeButtonGroup.getValue() === NormalWorkingTimeEvaluationMode.INDEPENDENT) {
				workingTimeType = {
					...workingTimeType,
					...this.setCreditabilityCompensationProperties(this.travellingTimeNWTIndependentCreditabilityCompensationComponent),
					travellingTimeTargetAccountWithinNormalWorkingTime: selectedTargetAccountWithin,
					travellingTimeTargetAccountOutsideNormalWorkingTime: selectedTargetAccountWithin
				};
			} else {
				workingTimeType = {
						...workingTimeType,
						...this.setCreditabilityCompensationProperties(this.travellingTimeOutsideNWTCreditabilityCompensationComponent),
						compensationType: this.travellingTimeOutsideNWTCreditabilityCompensationComponent.getMode() === CompensationCreditabilityMode.FullCreditability ? CompensationType.FULL_OUTSIDE_DAILY_NORMAL_WORKING_TIME : CompensationType.WITH_PERCENTAGE_CREDITABILITY_OUTSIDE_DAILY_NORMAL_WORKING_TIME,
						travellingTimeTargetAccountWithinNormalWorkingTime: selectedTargetAccountWithin,
						travellingTimeTargetAccountOutsideNormalWorkingTime: selectedTargetAccountOutside
					};
			}
		}

		// Handle proofRequirement and proofRequiredAfterDays to ensure the values are correctly set
		workingTimeType = {...workingTimeType,
			proofRequirement: workingTimeType.proofRequirement === ProofRequirement.REQUIRED_AFTER_DAYS ? ProofRequirement.REQUIRED : workingTimeType.proofRequirement,
			proofRequiredAfterDays: [ProofRequirement.REQUIRED_AFTER_DAYS, ProofRequirement.REQUIRED].includes(workingTimeType.proofRequirement) ? workingTimeType.proofRequiredAfterDays : null
		};

		// Handle workingTimeRequestStepsConfiguration
		const approvalStepsSelect: HTMLSelectElement | null = document.getElementById("selectIndividualApproval") as HTMLSelectElement;
		const isIndividualSelected = approvalStepsSelect?.value === "INDIVIDUAL";
		const isByRequest = workingTimeType.recordingModeUser === WorkingTimeTypeRecordingMode.BY_REQUEST
			|| workingTimeType.noticeMode === WorkingTimeNoticeMode.BY_REQUEST;

		if (isIndividualSelected && isByRequest) {
			// Get configuration from the component
			const configuration = this.workingTimeRequestStepsConfigurationComponent?.config || [];
			// @ts-expect-error This is just used for serialization, and the configuration has to be stringified
			workingTimeType = {...workingTimeType, workingTimeRequestStepsConfiguration: JSON.stringify(configuration)};
		} else {
			// Set to null to signal no individual config
			workingTimeType = {...workingTimeType, workingTimeRequestStepsConfiguration: null};
		}

		if (this.locationPanel && this.isGeofenceConfigurableForWtt()) {
			const { locationRestrictionRadius, locationRestrictionInherited: _unused, ...locationFields } = this.locationPanel.getData();
			workingTimeType = {
				...workingTimeType,
				location: locationFields,
				locationRestrictionRadius
			};
		}
		// else: location omitted → service preserves existing values

		return workingTimeType;
	}

	private setCreditabilityCompensationProperties(creditabilityCompensationComponent: CreditabilityCompensationComponent | null): WorkingTimeType {
		let workingTimeType: WorkingTimeType;
		const componentValues = creditabilityCompensationComponent?.values;
		const componentMode = creditabilityCompensationComponent?.getMode();

		switch (componentMode) {
			// If the mode is FullCreditability, set all creditability and deductible values to 1 and 0 respectively
			case CompensationCreditabilityMode.FullCreditability:
				workingTimeType = {
					...this.workingTimeType,
					creditability: 1,
					nonCreditableDeductible: 0,
					compensationCreditability: 1,
					compensationDeductible: 0,
					compensationType: CompensationType.FULL
				};
				break;
			// If the mode is Compensation, update the compensationCreditability based on the configured value or set it to 1
			case CompensationCreditabilityMode.Compensation: {
				const compensationRadio = creditabilityCompensationComponent.getSelectedCompensationType();
				if (compensationRadio === "compensationCreditability") {
					workingTimeType = {
						...this.workingTimeType,
						compensationCreditability: componentValues.compensationCreditability,
						compensationDeductible: 0,
						creditability: 1,
						nonCreditableDeductible: 0,
						compensationType: CompensationType.WITH_PERCENTAGE_CREDITABILITY
					};
				} else {
					workingTimeType = {
						...this.workingTimeType,
						compensationCreditability: 1,
						compensationDeductible: componentValues.compensationDeductible,
						creditability: 1,
						nonCreditableDeductible: 0,
						compensationType: CompensationType.WITH_DURATION_DEDUCTIBLE
					};
				}
				break;
			}
			// If the mode is Creditability, update the creditability based on the configured value or set it to 1
			case CompensationCreditabilityMode.Creditability: {
				const creditabilityRadio = creditabilityCompensationComponent.getSelectedCreditabilityType();
				if (creditabilityRadio === "creditability") {
					workingTimeType = {
						...this.workingTimeType,
						creditability: componentValues.creditability,
						nonCreditableDeductible: 0,
						compensationCreditability: 1,
						compensationDeductible: 0,
						compensationType: null
					};
				} else {
					workingTimeType = {
						...this.workingTimeType,
						creditability: 1,
						nonCreditableDeductible: componentValues.nonCreditableDeductible,
						compensationCreditability: 1,
						compensationDeductible: 0,
						compensationType: null
					};
				}
				break;
			}
		}
		return workingTimeType;
	}

	public resetWorkingTimeType() {
		$("#radioFullCreditable").prop("checked", true);
		this.locationPanel?.reset();

		this.setWorkingTimeType({
			id: null,
			workingTimeTypeId: "",
			name: "",
			shortName: "",
			description: "",
			version: null,
			externalId: null,
			category: WorkingTimeTypeCategory.AttendanceTime,
			subCategory: WorkingTimeTypeSubCategory.Present,
			editUnit: DurationUnit.Minutes,
			archived: false,
			recordingModeUser: WorkingTimeTypeRecordingMode.ALLOWED,
			creditability: 1,
			nonCreditableDeductible: 0,
			compensationCreditability: 1,
			compensationDeductible: 0,
			compensationType: CompensationType.FULL,
			normalWorkingTimeEvaluationMode: NormalWorkingTimeEvaluationMode.INDEPENDENT,
			initiated: false,
			requiresSubstitute: false,
			travellingTimeTargetAccountWithinNormalWorkingTime: TravellingTimeTargetAccount.TIME_ACCOUNT,
			travellingTimeTargetAccountOutsideNormalWorkingTime: TravellingTimeTargetAccount.TIME_ACCOUNT,
			automaticBreakDeductionPolicy: AutomaticBreakDeductionPolicy.DEDUCTION_ALLOWED,
			workingTimeRequestStepsConfiguration: null,
			workingTimeRequestStepsEmailNotificationEnabled: this.config.defaultWorkingTimeRequestStepsEmailNotificationEnabled,
			proofRequirement: ProofRequirement.NOT_APPLICABLE,
			proofRequiredAfterDays: null,
			noticeMode: WorkingTimeNoticeMode.NONE
		});

		$("#workingTimeTypeArchiveFlag").hide();
	}

	private setSubCategoryVisible(visible: boolean) {
		if (visible) {
			$("#subCategoryRow").toggle(true);
			$("#workingTimeTypeSubCategory").removeAttr("disabled");
		} else {
			$("#subCategoryRow").toggle(false);
			$("#workingTimeTypeSubCategory").attr("disabled", "disabled");
		}
	}

	private setAutomaticBreakDeductionPolicyVisible(visible: boolean) {
		if (visible) {
			$("#automaticBreakDeductionPolicyRow").toggle(true);
			$("#workingTimeTypeAutomaticBreakDeductionPolicy").removeAttr("disabled");
		} else {
			$("#automaticBreakDeductionPolicyRow").toggle(false);
			$("#workingTimeTypeAutomaticBreakDeductionPolicy").attr("disabled", "disabled");
		}
	}

	public setWorkingTimeType(workingTimeType: WorkingTimeType) {
		this.workingTimeType = copyOfObject(workingTimeType);
		this.workingTimeType.proofRequirement = this.workingTimeType.proofRequirement === ProofRequirement.REQUIRED && this.workingTimeType.proofRequiredAfterDays
			? ProofRequirement.REQUIRED_AFTER_DAYS
			: this.workingTimeType.proofRequirement;
		this.observer.setModel(this.workingTimeType);

		$("#selectIndividualApproval").val(this.workingTimeType.workingTimeRequestStepsConfiguration ? "INDIVIDUAL" : "DEFAULT");
		if (this.workingTimeRequestStepsConfigurationComponent) {
			this.workingTimeRequestStepsConfigurationComponent.config = this.workingTimeType.workingTimeRequestStepsConfiguration || this.config.defaultWorkingTimeRequestStepsConfiguration;
		}
		this.normalWorkingTimeEvaluationModeButtonGroup?.setValue(this.workingTimeType.normalWorkingTimeEvaluationMode);
		this.setComponentValues(this.creditabilityCompensationComponent, this.workingTimeType.creditability, this.workingTimeType.compensationCreditability, this.workingTimeType.nonCreditableDeductible, this.workingTimeType.compensationDeductible, this.workingTimeType.compensationType);
		this.setComponentValues(this.travellingTimeNWTIndependentCreditabilityCompensationComponent, this.workingTimeType.creditability, this.workingTimeType.compensationCreditability, this.workingTimeType.nonCreditableDeductible, this.workingTimeType.compensationDeductible, this.workingTimeType.compensationType);
		this.setComponentValues(this.travellingTimeOutsideNWTCreditabilityCompensationComponent, this.workingTimeType.creditability, this.workingTimeType.compensationCreditability, 0, 0, this.workingTimeType.compensationType);
		const h = [NormalWorkingTimeEvaluationMode.DURATION, NormalWorkingTimeEvaluationMode.POSITION_AND_DURATION].includes(this.workingTimeType.normalWorkingTimeEvaluationMode);
		$(`input[name="cappedWithDuration"][value="${h}"]`).prop("checked", true);

		this.updateTitle();
		this.updateFieldValues();
		this.setLocationTabFields(this.workingTimeType);
		$("#workingTimeTypeArchiveFlag").show();
		FormUtil.removeAllFeedback($("#workingTimeTypeDialog"), true);
	}

	public openDialog() {
		// set the visibility of the subcategory based on the working time type category
		this.setSubCategoryVisible(this.workingTimeType && this.workingTimeType.category === WorkingTimeTypeCategory.AttendanceTime);

		// set the visibility of the automatic break deduction policy based on the working time type category and edit unit
		this.updateAutomaticBreakDeductionPolicyVisibility(this.workingTimeType.category, this.workingTimeType.editUnit);

		// Component handles mode and radio selection internally based on values
		this.selectCompensationTypeRadio(this.workingTimeType.compensationType);

		// set the creditabilityChanged false
		// has to be done at the end as compensationCreditabilityMode.setValue() sets it to true
		this.compensationCreditabilityChanged = false;

		// reset the automaticBreakDeductionPolicyChanged flag
		this.automaticBreakDeductionPolicyChanged = false;

		// show the modal dialog
		this.modal.show();
	}

	public close() {
		this.modal.close();
	}

	private updateFieldValues() {
		this.modal.toggleButton("btn-outline-danger", this.workingTimeType.id != null);

		$("#workingTimeTypeId").val(this.workingTimeType.id);
		$("#workingTimeTypeName").val(this.workingTimeType.name ? this.workingTimeType.name : "");
		$("#workingTimeTypeShortName").val(this.workingTimeType.shortName ? this.workingTimeType.shortName : "");
		$("#workingTimeTypeDescription").val(this.workingTimeType.description ? this.workingTimeType.description : "");
		$("#workingTimeTypeVersion").val(this.workingTimeType.version);
		$("#workingTimeTypeExternalId").val(this.workingTimeType.externalId);
		$("#workingTimeTypeCategory").val(this.workingTimeType.category).prop("disabled", this.workingTimeType.id != null);
		$("#workingTimeTypeSubCategory").val(this.getSubCategoryForDisplay());
		$("#workingTimeTypeAutomaticBreakDeductionPolicy").val((this.workingTimeType.automaticBreakDeductionPolicy ?? AutomaticBreakDeductionPolicy.DEDUCTION_ALLOWED) as string);
		$("#workingTimeTypeArchived").prop("checked", this.workingTimeType.archived);
		$("#checkboxInitiated").prop("checked", this.workingTimeType.initiated).prop("disabled", this.workingTimeType.id != null);
		$("#workingTimeTypeRequiresSubstituteCheckbox").prop("checked", this.workingTimeType.requiresSubstitute);

		$("#workingTimeRequestStepsEmailNotificationEnabled").prop("checked", this.workingTimeType.workingTimeRequestStepsEmailNotificationEnabled);

		$("#workingTimeTypeProofRequirementRequiredAfterDaysInput").val(this.workingTimeType.proofRequiredAfterDays);

		let recordingModeUser = this.workingTimeType.recordingModeUser;
		if (recordingModeUser === WorkingTimeTypeRecordingMode.BY_REQUEST && !SecurityStatus.isWorkingTimeRequestsEnabled()) {
			recordingModeUser = WorkingTimeTypeRecordingMode.NOT_ALLOWED;
		}
		$(`input[name="recordingModeUser"][value="${recordingModeUser}"]`).prop("checked", true);

		// Proofs
		$(`input[name="proofRequirement"][value="${this.workingTimeType.proofRequirement}"]`).prop("checked", true);

		// Notice Mode
		this.fillNoticeModeSelect();
		this.updateNoticeModeVisibility();

		if (SecurityStatus.isTravellingTimeAllowed() && this.isTravellingTime()) {
			const {
				travellingTimeTargetAccountWithinNormalWorkingTime,
				travellingTimeTargetAccountOutsideNormalWorkingTime
			} = this.workingTimeType;
			$(`input[name="travellingTimeTargetAccountWithinNormalWorkingTime"][value="${travellingTimeTargetAccountWithinNormalWorkingTime ?? TravellingTimeTargetAccount.TIME_ACCOUNT}"]`).prop("checked", true).trigger('change');
			$(`input[name="travellingTimeTargetAccountOutsideNormalWorkingTime"][value="${travellingTimeTargetAccountOutsideNormalWorkingTime ?? TravellingTimeTargetAccount.TIME_ACCOUNT}"]`).prop("checked", true).trigger('change');
		}

		if (this.workingTimeType.category === WorkingTimeTypeCategory.PaidTimeOff) {
			this.syncPaidTimeOffNormalWorkingTimeEvaluationMode();
		}

		this.fillUnits();
		this.updateRecordingModeUserVisibility();
		this.updateProofRequirementVisibility();
		$("#creditabilityCompensationContainer").toggle(this.shouldShowCreditabilityCompensation());
		this.updateCompensationWorkingTimeConfiguration();
		this.updateWorkingTimeRequestStepsConfiguration();
		this.updateTravellingTimeVisibility();
	}

	private getSubCategoryForDisplay(): WorkingTimeTypeSubCategory | null {
		const subCategory = this.workingTimeType.subCategory;
		if (subCategory == null) {
			return null;
		}
		if (SecurityStatus.isTravellingTimeAllowed() || ![
			WorkingTimeTypeSubCategory.ActiveDrivingTime,
			WorkingTimeTypeSubCategory.PassiveTravellingTime
		].includes(subCategory)) {
			return subCategory;
		}
		// Downgraded fallback for hidden travelling time subcategories in the select.
		return WorkingTimeTypeSubCategory.Away;
	}

	private fillUnits() {
		const editUnitSelect = $("select#workingTimeTypeEditUnit");
		if (editUnitSelect.length > 0) {
			editUnitSelect.empty();

			const units: DurationUnit[] = CATEGORY_UNITS[this.workingTimeType.category].filter(
				unit => !this.workingTimeType.id || ((unit === DurationUnit.Minutes && this.workingTimeType.editUnit === DurationUnit.Minutes)
					|| (unit !== DurationUnit.Minutes && this.workingTimeType.editUnit !== DurationUnit.Minutes)));

			units.forEach(unit => {
				editUnitSelect.append($("<option></option>", {value: unit}).text(messages["durationUnit." + unit]));
			});

			if (this.workingTimeType.editUnit === null) {
				this.workingTimeType.editUnit = units[0];
			}

			$("#workingTimeTypeEditUnit")
				.val(this.workingTimeType.editUnit)
				.prop("disabled", units.length === 1);
		}
	}

	private initListener() {
		this.observer.observeInput("#workingTimeTypeId", "id");
		this.observer.observeInput("#workingTimeTypeName", "name");
		this.observer.observeInput("#workingTimeTypeShortName", "shortName");
		this.observer.observeInput("#workingTimeTypeDescription", "description");
		this.observer.observeInput("#workingTimeTypeVersion", "version");
		this.observer.observeInput("#workingTimeTypeExternalId", "externalId");
		this.observer.observeInput("#workingTimeTypeCategory", "category");
		this.observer.observeInput("#workingTimeTypeSubCategory", "subCategory");
		this.observer.observeInput("#workingTimeTypeEditUnit", "editUnit");
		this.observer.observeInput("#workingTimeTypeArchived", "archived");
		this.observer.observeInput("#workingTimeTypeAutomaticBreakDeductionPolicy", "automaticBreakDeductionPolicy");
		this.observer.observeInput("#checkboxInitiated", "initiated");
		this.observer.observeInput("input[name='recordingModeUser']", "recordingModeUser", selector => $(selector).filter(":checked").val() as WorkingTimeTypeRecordingMode);
		this.observer.observeInput("input[name='travellingTimeTargetAccountWithinNormalWorkingTime']", "travellingTimeTargetAccountWithinNormalWorkingTime",
			selector => $(selector).filter(":checked").val() as TravellingTimeTargetAccount);
		this.observer.observeInput("input[name='travellingTimeTargetAccountOutsideNormalWorkingTime']", "travellingTimeTargetAccountOutsideNormalWorkingTime",
			selector => $(selector).filter(":checked").val() as TravellingTimeTargetAccount);
		this.observer.observeInput("#workingTimeTypeProofRequirement", "proofRequirement");
		this.observer.observeInput("#workingTimeTypeProofRequirementRequiredAfterDaysInput", "proofRequiredAfterDays", FieldObserver.getNullableNumberFromField);
		this.observer.observeInput("#workingTimeTypeRequiresSubstituteCheckbox", "requiresSubstitute");
		this.observer.observeInput("#workingTimeTypeNoticeMode", "noticeMode");

		this.observer.observeInput("input[name='compensationType']", "compensationType", selector => $(selector).filter(":checked").val() as CompensationType);
		this.observer.observeInput("input[name='compensationTypePaidTimeOffDayBased']", "compensationType", selector => $(selector).filter(":checked").val() as CompensationType);

		this.observer.observeInput("#workingTimeRequestStepsEmailNotificationEnabled", "workingTimeRequestStepsEmailNotificationEnabled", FieldObserver.getCheckboxState);

		this.observer.onFieldValueChange((event, data) => {
			if (data.field === "category") {
				const category = data.newValue as WorkingTimeTypeCategory;
				const currentUnit = this.workingTimeType.editUnit;
				const units = CATEGORY_UNITS[category];

				if (units.indexOf(currentUnit) === -1) {
					this.workingTimeType.editUnit = units[0];
				}
				if (category === WorkingTimeTypeCategory.BreakTime) {
					this.workingTimeType.recordingModeUser = WorkingTimeTypeRecordingMode.ALLOWED;
				}

				this.updateRecordingModeUserVisibility();
				this.updateSubCategoryVisibility(category);
				this.updateAutomaticBreakDeductionPolicyVisibility(category, this.workingTimeType.editUnit);

				$("#containerInitiated").toggle(category === WorkingTimeTypeCategory.AttendanceTime);
				this.updateGeofenceTabVisibility();
				this.updateNoticeModeVisibility();
			}

			if (data.field === "subCategory") {
				this.updateGeofenceTabVisibility();
			}

			if (data.field === "editUnit") {
				this.updateRecordingModeUserVisibility();
				this.updateCompensationWorkingTimeConfiguration();
				this.updateAutomaticBreakDeductionPolicyVisibility(this.workingTimeType.category, this.workingTimeType.editUnit);
				this.updateGeofenceTabVisibility();
				this.updateNoticeModeVisibility();
			}

			if (data.field === "recordingModeUser") {
				this.updateRecordingModeInfoText();
				this.updateNoticeModeVisibility();
			}

			if (data.field === "proofRequirement") {
				this.updateProofRequirementVisibility();
			}

			this.updateFieldValues();

			if (data.field === "automaticBreakDeductionPolicy") {
				// update changed flag
				this.automaticBreakDeductionPolicyChanged = true;
			}
		});
		$(`input[name="cappedWithDuration"]`).on("change", () => this.updateFieldValues());
	}


	/**
	 * Selects the radio button corresponding to the given compensation credibility type.
	 * Clears the values of other inputs that do not match the selected type.
	 *
	 * @param type - The compensation credibility type to select.
	 */
	private selectCompensationTypeRadio(type: CompensationType): void {
		// select the radio button corresponding to the given type
		$(`input[type='radio'][value='${type}']`).prop("checked", true);
	}

	private syncPaidTimeOffNormalWorkingTimeEvaluationMode(): void {
		const compensationType = this.workingTimeType.compensationType;
		const cappedWithDuration = $(`input[name="cappedWithDuration"]:checked`).val() === "true";
		let normalWorkingTimeEvaluationMode = NormalWorkingTimeEvaluationMode.INDEPENDENT;
		if (this.isPaidTimeOffInEditUnit(DurationUnit.Minutes)) {
			if (cappedWithDuration && compensationType === CompensationType.FULL) {
				normalWorkingTimeEvaluationMode = NormalWorkingTimeEvaluationMode.DURATION;
			} else if (cappedWithDuration && compensationType === CompensationType.FULL_WITH_DAILY_NORMAL_WORKING_TIME_ONLY) {
				normalWorkingTimeEvaluationMode = NormalWorkingTimeEvaluationMode.POSITION_AND_DURATION;
			} else if (!cappedWithDuration && compensationType === CompensationType.FULL_WITH_DAILY_NORMAL_WORKING_TIME_ONLY) {
				normalWorkingTimeEvaluationMode = NormalWorkingTimeEvaluationMode.POSITION;
			}
		} else if (this.isPaidTimeOffInEditUnit(DurationUnit.Days)
			&& compensationType === CompensationType.FULL_WITH_DAILY_NORMAL_WORKING_TIME_ONLY) {
			normalWorkingTimeEvaluationMode = NormalWorkingTimeEvaluationMode.DURATION;
		}
		this.workingTimeType.normalWorkingTimeEvaluationMode = normalWorkingTimeEvaluationMode;
		$(`input[name="compensationType"][value="${compensationType}"]`).prop("checked", true);
		$(`input[name="cappedWithDuration"][value="${[NormalWorkingTimeEvaluationMode.DURATION, NormalWorkingTimeEvaluationMode.POSITION_AND_DURATION].includes(normalWorkingTimeEvaluationMode)}"]`).prop("checked", true);
	}


	private updateSubCategoryVisibility(workingTimeTypeCategory: WorkingTimeTypeCategory) {
		this.setSubCategoryVisible(workingTimeTypeCategory === WorkingTimeTypeCategory.AttendanceTime);
	}

	private updateAutomaticBreakDeductionPolicyVisibility(workingTimeTypeCategory: WorkingTimeTypeCategory, editUnit: DurationUnit) {
		this.setAutomaticBreakDeductionPolicyVisible(workingTimeTypeCategory === WorkingTimeTypeCategory.AttendanceTime && editUnit === DurationUnit.Minutes)
	}

	private updateRecordingModeUserVisibility() {
		$("[data-show-for-day-based]").toggle(this.workingTimeType.editUnit === DurationUnit.Days || this.workingTimeType.editUnit === DurationUnit.HalfDays);
		$("[data-show-for-recording-request]").toggle(this.workingTimeType.recordingModeUser === WorkingTimeTypeRecordingMode.BY_REQUEST);
		$("[data-show-for-recording-or-notice-request]").toggle(
			this.workingTimeType.recordingModeUser === WorkingTimeTypeRecordingMode.BY_REQUEST
			|| this.workingTimeType.noticeMode === WorkingTimeNoticeMode.BY_REQUEST
		);
		$("[data-hide-for-break-time]").toggle(this.workingTimeType.category !== WorkingTimeTypeCategory.BreakTime);

		this.updateRecordingModeInfoText();
	}

	private fillProofRequirementSelect() {
		const select = $("#workingTimeTypeProofRequirement");
		if (select.length === 0) {
			return;
		}
		let recordingModeUser = this.workingTimeType.recordingModeUser;
		if (recordingModeUser === WorkingTimeTypeRecordingMode.BY_REQUEST && !SecurityStatus.isWorkingTimeRequestsEnabled()) {
			recordingModeUser = WorkingTimeTypeRecordingMode.NOT_ALLOWED;
		}
		const allowedOptions = getProofRequirementOptions(recordingModeUser, this.workingTimeType.editUnit);
		const isDisabled = allowedOptions.length === 1;

		select.empty();
		allowedOptions.forEach(value => {
			select.append($("<option></option>", {value}).text(messages["workingTimeType.proofRequirement." + value]));
		});

		const valueToSet = allowedOptions.includes(this.workingTimeType.proofRequirement)
			? this.workingTimeType.proofRequirement
			: ProofRequirement.NOT_APPLICABLE;
		const changed = this.workingTimeType.proofRequirement !== valueToSet;
		this.workingTimeType.proofRequirement = valueToSet;
		select.val(valueToSet).prop("disabled", isDisabled);
		if (changed) {
			select.trigger("change");
		}
	}

	private updateProofRequirementVisibility() {
		this.fillProofRequirementSelect();
		const calendarDaysContainer = $("#workingTimeTypeProofRequirementRequiredAfterDaysContainer");

		// Reset proofRequiredAfterDays if proofRequirement is not REQUIRED_AFTER_DAYS
		if (this.workingTimeType.proofRequirement !== ProofRequirement.REQUIRED_AFTER_DAYS) {
			this.workingTimeType.proofRequiredAfterDays = 0;
			calendarDaysContainer.hide().find("input").val(null);
		} else {
			calendarDaysContainer.show();
		}
	}

	private fillNoticeModeSelect() {
		const select = $("#workingTimeTypeNoticeMode");
		if (select.length === 0) {
			return;
		}

		select.empty();
		Object.values(WorkingTimeNoticeMode).forEach(value => {
			select.append($("<option></option>", {value}).text(messages["workingTimeType.noticeMode." + value]));
		});

		select.val(this.workingTimeType.noticeMode);
	}

	private updateNoticeModeVisibility() {
		const isVisible = this.isNoticeConfigurationAllowed(this.workingTimeType.category)
			&& this.workingTimeType.recordingModeUser !== WorkingTimeTypeRecordingMode.BY_REQUEST;
		$("#containerNoticeMode").toggle(isVisible);
		if (!isVisible && this.workingTimeType.noticeMode !== WorkingTimeNoticeMode.NONE) {
			this.workingTimeType.noticeMode = WorkingTimeNoticeMode.NONE;
			$("#workingTimeTypeNoticeMode").val(WorkingTimeNoticeMode.NONE);
		}
	}

	// Notices have no use case for break times, unpaid absences, suspensions, and vacation (TIMR-12316),
	// so the configuration is only offered for attendance time and paid time off.
	// See WorkingTimeTypeService.isNoticeConfigurationAllowed
	private isNoticeConfigurationAllowed(category: WorkingTimeTypeCategory): boolean {
		return category === WorkingTimeTypeCategory.AttendanceTime
			|| category === WorkingTimeTypeCategory.PaidTimeOff
	}

	private updateRecordingModeInfoText() {
		const isRecordingModeNotAllowed = this.workingTimeType.recordingModeUser === WorkingTimeTypeRecordingMode.NOT_ALLOWED;

		$("#workingTimeTypeRecordingModeAdminInfo").toggle(isRecordingModeNotAllowed && !SecurityStatus.isCorporateFeaturesAllowed());
		$("#workingTimeTypeRecordingModeTeamleaderInfo").toggle(isRecordingModeNotAllowed && SecurityStatus.isCorporateFeaturesAllowed());
	}

	public updateCompensationWorkingTimeConfiguration() {
		const compensationConfigurationForPaidTimeOffMinuteBasedContainer = $("#compensationConfigurationForPaidTimeOffMinuteBased");
		const compensationConfigurationForPaidTimeOffDayBasedContainer = $("#compensationConfigurationForPaidTimeOffDayBased");
		if (SecurityStatus.isPaidTimeOffCompensationAllowed()) {
			compensationConfigurationForPaidTimeOffMinuteBasedContainer.toggle(this.isPaidTimeOffInEditUnit(DurationUnit.Minutes));
			compensationConfigurationForPaidTimeOffDayBasedContainer.toggle(this.isPaidTimeOffInEditUnit(DurationUnit.Days));
		} else {
			compensationConfigurationForPaidTimeOffMinuteBasedContainer.hide();
			compensationConfigurationForPaidTimeOffDayBasedContainer.hide();
		}
	}

	public creditabilityConfigChanged() {
		this.compensationCreditabilityChanged = true;
	}

	private updateTitle() {
		const message = messages[this.workingTimeType.id == null ? "workingTimeType.header.create" : "workingTimeType.header.edit"];

		this.modal.setTitle(message, true);
	}

	private isPaidTimeOffInEditUnit(editUnit: DurationUnit) {
		return this.workingTimeType.category === WorkingTimeTypeCategory.PaidTimeOff && this.workingTimeType.editUnit === editUnit;
	}

	private isDefaultCompensationTypeForNonAttendance(compensationType: CompensationType | null): boolean {
		return compensationType === CompensationType.FULL
			|| compensationType === CompensationType.FULL_WITH_DAILY_NORMAL_WORKING_TIME_ONLY;
	}

	private shouldShowCreditabilityCompensation() {
		if (!SecurityStatus.isEnhancedWorkingTimeTypesAllowed() || this.isTravellingTime()) {
			return false;
		}
		if (this.workingTimeType.category === WorkingTimeTypeCategory.AttendanceTime) {
			return true;
		}

		return !this.isDefaultCompensationTypeForNonAttendance(this.workingTimeType.compensationType);
	}

	private isTravellingTime() {
		return this.workingTimeType.category === WorkingTimeTypeCategory.AttendanceTime &&
			(this.workingTimeType.subCategory === WorkingTimeTypeSubCategory.ActiveDrivingTime ||
				this.workingTimeType.subCategory === WorkingTimeTypeSubCategory.PassiveTravellingTime);
	}

	private updateWorkingTimeRequestStepsConfiguration() {
		const approvalStepsSelect: HTMLSelectElement | null = document.getElementById("selectIndividualApproval") as HTMLSelectElement;
		const isIndividualSelected = approvalStepsSelect?.value === "INDIVIDUAL";
		const isByRequest = this.workingTimeType.recordingModeUser === WorkingTimeTypeRecordingMode.BY_REQUEST
			|| this.workingTimeType.noticeMode === WorkingTimeNoticeMode.BY_REQUEST;
		$("[data-visible-for-individual]").toggle(isByRequest && isIndividualSelected);
	}

	private initTravellingTimeNormalWorkingTimeSelection() {
		this.normalWorkingTimeEvaluationModeButtonGroup = ButtonRadioGroup.typed<NormalWorkingTimeEvaluationMode>(
			{
				value: NormalWorkingTimeEvaluationMode.INDEPENDENT,
				button: document.getElementById("buttonBookingToNormalWorkingTimeIndependent")
			},
			{
				value: NormalWorkingTimeEvaluationMode.POSITION,
				button: document.getElementById("buttonBookingToNormalWorkingTimePosition")
			},
			{
				value: NormalWorkingTimeEvaluationMode.DURATION,
				button: document.getElementById("buttonBookingToNormalWorkingTimeDuration")
			}
		);
		this.normalWorkingTimeEvaluationModeButtonGroup
			.onValueChanged((selection: NormalWorkingTimeEvaluationMode) => {
				if (this.workingTimeType) {
					this.workingTimeType.normalWorkingTimeEvaluationMode = selection;
				}
				$("[data-show-for-normal-working-time-evaluation-mode]").each((_, el) => {
					const $el = $(el);
					$el.toggle($el.attr("data-show-for-normal-working-time-evaluation-mode")?.includes(selection));
				});
			})
			.setValue(NormalWorkingTimeEvaluationMode.INDEPENDENT);
		this.travellingTimeNWTIndependentCreditabilityCompensationComponent =
			document.getElementById("travellingTimeNWTIndependentCreditabilityCompensationComponent") as CreditabilityCompensationComponent;
		this.travellingTimeOutsideNWTCreditabilityCompensationComponent =
			document.getElementById("travellingTimeOutsideNWTCreditabilityCompensationComponent") as CreditabilityCompensationComponent;

		this.addCreditabilityChangeListener(this.travellingTimeNWTIndependentCreditabilityCompensationComponent);
		this.addCreditabilityChangeListener(this.travellingTimeOutsideNWTCreditabilityCompensationComponent);
	}

	private updateTravellingTimeVisibility() {
		if (SecurityStatus.isTravellingTimeAllowed()) {
			$("[data-show-for-travelling-time]")
				.toggle(this.isTravellingTime());
		} else {
			$("[data-show-for-travelling-time]").hide();
		}
	}

	private addCreditabilityChangeListener(component: CreditabilityCompensationComponent | null) {
		if (component) {
			$(component).on(CreditabilityCompensationComponent.EVENT_CHANGED, () => {
				this.creditabilityConfigChanged();
			});
		}
	}

	private setComponentValues(component: CreditabilityCompensationComponent | null, creditability: number = 1,
							   compensationCreditability: number = 1, nonCreditableDeductible: number = 0,
							   compensationDeductible: number = 0, compensationType: CompensationType = CompensationType.FULL) {
		if (component) {
			component.values = {
				creditability: creditability,
				compensationCreditability: compensationCreditability,
				nonCreditableDeductible: nonCreditableDeductible,
				compensationDeductible: compensationDeductible,
				compensationType: compensationType
			};
		}
	}

	private initPopups() {
		$("#workingTimeTypeProofRequirementRequiredAfterDaysInfo").popover({
			trigger: "hover",
			template: '<div class="popover popover-task" role="tooltip"><div class="arrow"></div><div class="popover-content"></div></div>',
			delay: { show: 500, hide: 0 },
			placement: "top",
			container: "body",
			content: messages["workingTimeType.proofRequirement.afterDays.info"]
		});
	}

	private initLocationTabFields() {
		const panel = document.getElementById("wttLocationPanel") as LocationPanel | null;
		if (!panel) {
			return;
		}
		this.locationPanel = panel;
		this.locationPanel.setReadonly(false);

		$('a[data-toggle="tab"]').on("shown.bs.tab", (e) => {
			if ($(e.target).attr("href") === "#wttTabPosition") {
				this.locationPanel?.refreshMap();
			}
		});
	}

	private setLocationTabFields(wtt: WorkingTimeType) {
		if (!this.locationPanel) {
			return;
		}
		this.updateGeofenceTabVisibility();
		const location = wtt.location ?? {};
		this.locationPanel.setData({
			...location,
			countryId: location.countryId ?? this.config.billingAddressCountryId,
			locationRestrictionRadius: wtt.locationRestrictionRadius
		});
	}

	private updateGeofenceTabVisibility() {
		if (!this.locationPanel) {
			return;
		}
		const configurable = this.isGeofenceConfigurableForWtt();
		$("#wttGeofenceNotAvailableInfo").toggle(!configurable);
		$("#wttGeofenceContent").toggle(configurable);
	}

	private isGeofenceConfigurableForWtt(): boolean {
		const { category, editUnit } = this.workingTimeType;
		return category === WorkingTimeTypeCategory.AttendanceTime &&
			editUnit === DurationUnit.Minutes;
	}
}
